import { useEffect, useRef, useState } from 'react';
import { diagnosticoAPI, messageAPI } from '../services/api.js';
import { api } from '../api/client.js';
import {
    GUIA_AYUDA,
    GUIA_CIERRE,
    GUIA_EPA,
    GUIA_FATIGA,
    GUIA_LLEGADA,
    bumpEpaPauseHabit,
    firstNameOfUser,
    matchVoiceOption,
    openingLine,
    setEpaHabitReminder,
    storeLocalVoiceSignal,
} from '../data/jornadaConversationScript.js';
import { captureVoiceIndices } from '../utils/voiceFeatures.js';
import {
    canListen,
    cancelSpeech,
    speakEs,
    startOneShotListen,
} from '../utils/voiceUx.js';
import './JornadaConversation.css';

const PAUSE_SEC = 30;

function bubble(id, from, text) {
    return { id, from, text };
}

export default function JornadaConversation({ user }) {
    const name = firstNameOfUser(user);
    const [messages, setMessages] = useState(() => [bubble('g0', 'guide', openingLine(name))]);
    const [step, setStep] = useState('arrival');
    const [busy, setBusy] = useState(false);
    const [listening, setListening] = useState(false);
    const [voiceHint, setVoiceHint] = useState('');
    const [fatigueKey, setFatigueKey] = useState(null);
    const [ttsOn, setTtsOn] = useState(true);
    const [pauseLeft, setPauseLeft] = useState(PAUSE_SEC);
    const listRef = useRef(null);
    const stopListenRef = useRef(null);
    const ttsOnRef = useRef(true);

    const voiceOk = canListen();

    useEffect(() => {
        ttsOnRef.current = ttsOn;
        if (!ttsOn) cancelSpeech();
    }, [ttsOn]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages, step, pauseLeft]);

    useEffect(() => {
        speakEs(openingLine(name), true);
        return () => {
            cancelSpeech();
            stopListenRef.current?.();
        };
        // once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (step !== 'epa_pause') return undefined;
        setPauseLeft(PAUSE_SEC);
        speakEs(
            'Pausa guiada. Hombros abajo, mandíbula suelta. Una respiración un poco más larga. Te acompaño treinta segundos.',
            ttsOnRef.current
        );
        const id = window.setInterval(() => {
            setPauseLeft((n) => {
                if (n <= 1) {
                    window.clearInterval(id);
                    return 0;
                }
                return n - 1;
            });
        }, 1000);
        return () => window.clearInterval(id);
    }, [step]);

    useEffect(() => {
        if (step === 'epa_pause' && pauseLeft === 0) {
            void finishGuidedPause();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pauseLeft, step]);

    function add(from, text) {
        setMessages((prev) => [...prev, bubble(`${from}-${Date.now()}`, from, text)]);
        if (from === 'guide') speakEs(text, ttsOnRef.current);
    }

    async function saveVoiceIfPossible() {
        try {
            const result = await captureVoiceIndices({
                durationMs: 2800,
                scriptId: 'guia-jornada-v1',
            });
            storeLocalVoiceSignal(result.indices, result.quality);
            try {
                await api.postFunctionalSnapshot({
                    kind: 'conversational_voice',
                    coverage: { dimensions: { biometric_voice: true } },
                    dims: {
                        biometric: {
                            voice: {
                                indices: result.indices,
                                vector: result.vector,
                                quality: result.quality,
                            },
                        },
                    },
                });
            } catch {
                /* EF puede estar OFF: queda señal local */
            }
            try {
                await api.postLabsVoiceIndices({
                    source: 'jornada_guia',
                    scriptId: 'guia-jornada-v1',
                    indices: result.indices,
                    quality: result.quality,
                    vector: result.vector,
                });
            } catch {
                /* labs opcional */
            }
            if (result.quality?.ok) {
                setVoiceHint(
                    `Señal de voz: intensidad ${result.indices.intensity} · profundidad ${result.indices.depth} · shimmer ${result.indices.shimmerProxy}`
                );
            }
        } catch {
            /* mic ocupado por reconocimiento: no bloquear */
        }
    }

    async function onArrival(opt) {
        if (busy) return;
        setBusy(true);
        add('user', `${opt.emoji} ${opt.label}`);
        try {
            await diagnosticoAPI.submitCheckin({
                tipo: 'inicio',
                estado_animo: opt.feeling,
                causa_estado: opt.reason,
            });
            add('guide', `Quedó: ${opt.label}. Gracias por decirlo con honestidad. ¿Qué te ayudaría ahora?`);
            setStep('help');
        } catch (err) {
            add('guide', err?.message || 'No se pudo guardar. Puedes volver a la jornada de siempre.');
        } finally {
            setBusy(false);
        }
    }

    function onHelp(opt) {
        if (busy) return;
        add('user', `${opt.emoji} ${opt.label}`);
        add('guide', 'Seguimos con el cuerpo, como el resto del día. ¿Cómo te sientes ahora? (incluye indispuesto)');
        setStep('epa');
    }

    async function onEpa(opt) {
        if (busy) return;
        setBusy(true);
        add('user', `${opt.emoji} ${opt.label}`);
        try {
            await api.submitCheckin({ feeling: opt.key, need: opt.key === 'mal' ? 'descanso' : null });
            add(
                'guide',
                opt.key === 'sick'
                    ? 'Quedó indispuesto. Cuida la seguridad. Ahora la pausa guiada: solo respirar conmigo.'
                    : `Quedó: ${opt.label}. Ahora la pausa guiada: solo respirar conmigo.`
            );
            setStep('epa_pause');
        } catch (err) {
            add('guide', err?.message || 'No se pudo guardar EPA. Seguimos con la pausa guiada.');
            setStep('epa_pause');
        } finally {
            setBusy(false);
        }
    }

    async function finishGuidedPause() {
        const count = bumpEpaPauseHabit();
        try {
            await api.recordPause();
        } catch {
            /* no bloquear el hábito si falla el POST */
        }
        setEpaHabitReminder(true);
        add(
            'guide',
            `Pausa lista. Eso se vuelve hábito al repetirlo, no al hacerlo perfecto. Llevas ${count} día(s) con pausa registrada.`
        );
        await offerMessage();
    }

    async function offerMessage() {
        try {
            const res = await messageAPI.getHoy();
            const content = res?.data?.data?.content || res?.data?.content;
            if (content) {
                add('guide', `Un mensaje para ti: ${content}\n\nSi quieres, cerramos el turno. ¿Cómo vas de cansancio?`);
            } else {
                add('guide', 'Si quieres, cerramos el turno. ¿Cómo vas de cansancio?');
            }
        } catch {
            add('guide', 'Si quieres, cerramos el turno. ¿Cómo vas de cansancio?');
        }
        setStep('fatigue');
    }

    function onFatigue(opt) {
        if (busy) return;
        if (opt.key === 'skip') {
            add('user', 'Dejar para después');
            add('guide', 'Sin problema. La jornada de tarjetas sigue igual.');
            setStep('done');
            return;
        }
        setFatigueKey(opt.key);
        add('user', `${opt.emoji} ${opt.label}`);
        add('guide', '¿Te queda algo del trabajo dando vueltas?');
        setStep('closure');
    }

    async function onClosure(opt) {
        if (busy) return;
        setBusy(true);
        add('user', `${opt.emoji} ${opt.label}`);
        try {
            await diagnosticoAPI.submitCheckin({
                tipo: 'fin',
                estado_animo: opt.key,
                fatiga: fatigueKey,
            });
            add('guide', 'Listo por hoy. Gracias. Puedes volver a la jornada de siempre cuando quieras.');
            setStep('done');
        } catch (err) {
            add('guide', err?.message || 'No se pudo guardar el cierre.');
        } finally {
            setBusy(false);
        }
    }

    function optionsForStep() {
        if (step === 'arrival') return { list: GUIA_LLEGADA, handler: onArrival };
        if (step === 'help') return { list: GUIA_AYUDA, handler: onHelp };
        if (step === 'epa') return { list: GUIA_EPA, handler: onEpa };
        if (step === 'fatigue') {
            return {
                list: [...GUIA_FATIGA, { key: 'skip', label: 'Dejar para después', emoji: '➡️' }],
                handler: onFatigue,
            };
        }
        if (step === 'closure') return { list: GUIA_CIERRE, handler: onClosure };
        return { list: [], handler: () => {} };
    }

    function startVoice() {
        if (!voiceOk || busy || step === 'done' || step === 'epa_pause') return;
        setVoiceHint('Te escucho. Habla con naturalidad.');
        stopListenRef.current?.();
        void saveVoiceIfPossible();
        setListening(true);
        stopListenRef.current = startOneShotListen({
            onResult: (said) => {
                const { list, handler } = optionsForStep();
                const hit = matchVoiceOption(said, list);
                if (hit) handler(hit);
                else setVoiceHint(`Escuché: “${said}”. Toca una opción si no coincidió.`);
            },
            onError: () => setVoiceHint('No pude oír. Toca una opción o prueba Hablar otra vez.'),
            onEnd: () => setListening(false),
        }).stop;
    }

    const { list, handler } = optionsForStep();

    return (
        <div className="jguia">
            <div className="jguia-tools">
                <button
                    type="button"
                    className={`jguia-tts${ttsOn ? ' on' : ''}`}
                    onClick={() => setTtsOn((v) => !v)}
                >
                    {ttsOn ? 'Leyendo en voz alta' : 'Leer en voz alta: off'}
                </button>
            </div>
            <div className="jguia-thread" ref={listRef}>
                {messages.map((m) => (
                    <div key={m.id} className={`jguia-bubble jguia-${m.from} jguia-in`}>
                        {m.from === 'guide' ? <span className="jguia-who">Guía ALINEA</span> : null}
                        <p>{m.text}</p>
                    </div>
                ))}
            </div>

            {step === 'epa_pause' ? (
                <div className="jguia-pause" aria-live="polite">
                    <p className="jguia-pause-title">Pausa guiada</p>
                    <p className="jguia-pause-count">{pauseLeft}s</p>
                    <div className="jguia-pause-bar">
                        <span style={{ width: `${((PAUSE_SEC - pauseLeft) / PAUSE_SEC) * 100}%` }} />
                    </div>
                    <p className="jguia-hint">No hace falta tocar nada. Solo respira. Esto es el hábito.</p>
                </div>
            ) : null}

            {step !== 'done' && step !== 'epa_pause' && list.length > 0 ? (
                <div className="jguia-actions jguia-in">
                    {list.map((opt) => (
                        <button
                            key={opt.key}
                            type="button"
                            className="jguia-chip"
                            disabled={busy}
                            onClick={() => handler(opt)}
                        >
                            <span className="jguia-emoji" aria-hidden>
                                {opt.emoji}
                            </span>
                            <span className="jguia-chip-label">{opt.label}</span>
                        </button>
                    ))}
                    {voiceOk ? (
                        <button
                            type="button"
                            className={`jguia-mic${listening ? ' on' : ''}`}
                            disabled={busy}
                            onClick={startVoice}
                        >
                            <span className="jguia-emoji" aria-hidden>
                                {listening ? '🎤' : '🗣️'}
                            </span>
                            <span className="jguia-chip-label">
                                {listening ? 'Escuchando…' : 'Hablar'}
                            </span>
                        </button>
                    ) : (
                        <p className="jguia-hint">Toca una opción. Hablar pide Chrome o Safari.</p>
                    )}
                </div>
            ) : null}
            {voiceHint ? <p className="jguia-hint">{voiceHint}</p> : null}
            {busy ? <p className="jguia-hint">Guardando…</p> : null}
        </div>
    );
}
